// Legal Cases Hooks
// BP-10-04: Attorney Frontend

import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { casesApi } from '@/features/attorney/api/attorneyApi';
import type {
  LegalCase,
  LegalCaseFormData,
  CaseFilters,
  PaginatedCases,
  AttorneyDashboardStats,
} from '@/features/attorney/types/attorney';

// Query Keys
export const casesKeys = {
  all: ['cases'] as const,
  lists: () => [...casesKeys.all, 'list'] as const,
  list: (filters?: CaseFilters) => [...casesKeys.lists(), filters] as const,
  details: () => [...casesKeys.all, 'detail'] as const,
  detail: (id: string) => [...casesKeys.details(), id] as const,
  dashboard: () => [...casesKeys.all, 'dashboard'] as const,
};

/**
 * Hook to fetch paginated cases
 */
export function useCases(page = 1, pageSize = 20, filters?: CaseFilters) {
  return useQuery({
    queryKey: casesKeys.list(filters),
    queryFn: () => casesApi.getCases(page, pageSize, filters),
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to fetch cases with infinite scrolling
 */
export function useInfiniteCases(pageSize = 20, filters?: CaseFilters) {
  return useInfiniteQuery({
    queryKey: [...casesKeys.list(filters), 'infinite'],
    queryFn: ({ pageParam = 1 }) => casesApi.getCases(pageParam, pageSize, filters),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });
}

/**
 * Hook to fetch a single case
 */
export function useCase(caseId: string | undefined) {
  return useQuery({
    queryKey: casesKeys.detail(caseId!),
    queryFn: () => casesApi.getCase(caseId!),
    enabled: !!caseId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to fetch dashboard statistics
 */
export function useDashboardStats() {
  return useQuery({
    queryKey: casesKeys.dashboard(),
    queryFn: casesApi.getDashboardStats,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new case
 */
export function useCreateCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LegalCaseFormData) => casesApi.createCase(data),
    onSuccess: (newCase) => {
      // Add to cache
      queryClient.setQueryData(casesKeys.detail(newCase.id), newCase);
      // Invalidate list
      queryClient.invalidateQueries({ queryKey: casesKeys.lists() });
      // Invalidate dashboard
      queryClient.invalidateQueries({ queryKey: casesKeys.dashboard() });
    },
  });
}

/**
 * Hook to update a case
 */
export function useUpdateCase(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<LegalCaseFormData>) => casesApi.updateCase(caseId, data),
    onSuccess: (updatedCase) => {
      queryClient.setQueryData(casesKeys.detail(caseId), updatedCase);
      queryClient.invalidateQueries({ queryKey: casesKeys.lists() });
    },
  });
}

/**
 * Hook to archive a case
 */
export function useArchiveCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (caseId: string) => casesApi.archiveCase(caseId),
    onSuccess: (_, caseId) => {
      queryClient.removeQueries({ queryKey: casesKeys.detail(caseId) });
      queryClient.invalidateQueries({ queryKey: casesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: casesKeys.dashboard() });
    },
  });
}

/**
 * Hook to link a family to a case
 */
export function useLinkFamily(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (familyId: string) => casesApi.linkFamily(caseId, familyId),
    onSuccess: (updatedCase) => {
      queryClient.setQueryData(casesKeys.detail(caseId), updatedCase);
    },
  });
}

/**
 * Hook to unlink a family from a case
 */
export function useUnlinkFamily(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => casesApi.unlinkFamily(caseId),
    onSuccess: (updatedCase) => {
      queryClient.setQueryData(casesKeys.detail(caseId), updatedCase);
    },
  });
}

/**
 * Hook to download case summary
 */
export function useDownloadCaseSummary() {
  return useMutation({
    mutationFn: async (caseId: string) => {
      const blob = await casesApi.getCaseSummary(caseId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `case-summary-${caseId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });
}

/**
 * Helper to prefetch a case
 */
export function usePrefetchCase() {
  const queryClient = useQueryClient();

  return (caseId: string) => {
    queryClient.prefetchQuery({
      queryKey: casesKeys.detail(caseId),
      queryFn: () => casesApi.getCase(caseId),
      staleTime: 2 * 60 * 1000,
    });
  };
}


